'use client';

import { useAtom } from 'jotai';
import type { Session } from 'src/app/api/auth/auth-client';
import { useSignInOnClick } from 'src/hooks/useSignInOnClick';
import { useSignOutOnClick } from 'src/hooks/useSignOutOnClick';
import { sidebarAtom } from 'src/services/state/atoms/sidebar';
import { useSession } from 'src/services/state/providers/SessionProvider';
import { useWindowSize } from 'src/services/windowUtils';

import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';

import CustomLink from 'src/components/CustomLink';
import BrandBanner from './BrandBanner';
import { getFeatureFlag } from './FeatureFlags/FeatureFlags';
import Icon from './Icon';
import LocaleSwitcher from './LocaleSwitcher';
import SiteLogo from './SiteLogo';

/**
 * Builds the primary navigation links based on the user's session.
 *
 * @param {Session | null} [session] - The user's authentication session.
 * @returns {Array<{ i18nKey: string; href: string; icon: string; present: boolean }>}
 *          An array of navigation link objects.
 */
function buildPrimaryNavLinks(
  session: Session | null | undefined,
  onSignIn: () => void,
  onSignOut: () => void,
  windowWidth: number | undefined
) {
  const isDesktop = windowWidth !== undefined && windowWidth >= 1024; // Current breakpoint for when menu items are moved to a hamburger

  return [
    {
      i18nKey: 'nav_link_support',
      href: '/support',
      icon: 'support_agent',
      present: false, //true
      click: undefined,
    },
    {
      i18nKey: 'nav_link_my_documents',
      href: '/documents',
      icon: 'folder',
      present: !!session && isDesktop,
      click: undefined,
      classNames: 'desktop:display-inline-flex display-none',
    },
    {
      i18nKey: session ? 'nav_link_signout' : 'nav_link_signin',
      href: '#',
      icon: session ? 'logout' : 'login',
      present: isDesktop,
      click: session ? onSignOut : onSignIn,
      classNames: 'desktop:display-inline-flex display-none',
    },
  ];
}

/**
 * The Header component renders the top navigation and branding elements.
 *
 * @returns {JSX.Element} The rendered Header component.
 */
const Header = (): React.JSX.Element => {
  const session = useSession();
  const signInOnClick = useSignInOnClick();
  const signOutOnClick = useSignOutOnClick();
  const { width } = useWindowSize();

  const primaryLinks = buildPrimaryNavLinks(
    session,
    signInOnClick,
    () => {
      void signOutOnClick();
    },
    width
  );

  const t = useTranslations('components.Header');

  const [sidebar, setSidebar] = useAtom(sidebarAtom);
  const [displayHeader, setDisplayHeader] = useState(!sidebar.visible);

  // Add a delay to allow the sidebar to get out of the way before showing the header
  // Otherwise the side bar will appear to "jump" down when closed
  useEffect(() => {
    if (sidebar.visible) {
      setDisplayHeader(false);
    } else {
      const timer = setTimeout(() => setDisplayHeader(true), 250);
      return () => clearTimeout(timer);
    }
  }, [sidebar.visible]);

  /**
   * Filters the primary navigation links to only show the ones that should be displayed,
   * then maps them into JSX elements.
   */
  const navItems = primaryLinks
    .filter((link) => link.present)
    .map((link) => (
      <CustomLink
        button
        href={link.href}
        key={link.href}
        onClick={link.click}
        className={`width-auto  margin-left-2 ${link.classNames ?? ''}`}
        inversed
        variation="unstyled"
        aria-label={t('navigation_link_header_aria_label', { label: t(link.i18nKey) })}
      >
        <Icon name={link.icon} className="margin-right-05 position-relative text-middle" size={3} />
        {t(link.i18nKey)}
      </CustomLink>
    ));

  const pageBanner = getFeatureFlag('PAGE_BANNER');

  return (
    <>
      {pageBanner && (
        <div className="usa-alert usa-alert--warning margin-top-0">
          <div className="usa-alert__body">
            <h4 className="usa-alert__heading">Warning</h4>
            <p className="usa-alert__text">{pageBanner}</p>
          </div>
        </div>
      )}
      <BrandBanner />
      <div className="ma__header_slim">
        <div className="ma__header_slim__utility">
          <div className="ma__header_slim__utility-container ma__container">
            <div className="flex-fill margin-y-1">
              <div className="grid-row grid-gap">
                <div
                  className="grid-col-6 text-left"
                  style={{
                    height: '30px',
                    paddingTop: '3px',
                  }}
                >
                  <CustomLink
                    button
                    href="#"
                    onClick={() =>
                      setSidebar((prev) => ({
                        visible: !prev.visible,
                      }))
                    }
                    aria-expanded={sidebar.visible}
                    aria-controls="mobile-sidebar"
                    aria-label={t(sidebar.visible ? 'nav_menu_close_aria_label' : 'nav_menu_open_aria_label')}
                    className="desktop:display-none text-no-underline text-bold"
                    inversed
                    variation="unstyled"
                  >
                    <Icon
                      name={sidebar.visible ? 'close' : 'menu'}
                      className="text-base-lighter text-middle"
                      size={3}
                    />
                    {sidebar.visible ? t('nav_menu_close') : t('nav_menu_toggle')}
                  </CustomLink>
                </div>
                <div
                  className="grid-col-6 text-right"
                  style={{
                    height: '30px',
                    paddingTop: '3px',
                  }}
                >
                  <span className="display-inline-block" data-testid="language_selector">
                    <LocaleSwitcher />
                  </span>
                  {navItems}
                </div>
              </div>
            </div>
          </div>
        </div>
        <header
          className={`ma__header_slim__header${displayHeader ? ' ma__header_slim__header--visible' : ''}`}
          id="header"
          aria-hidden={!displayHeader}
        >
          <div className="ma__header_slim__header-container ma__container">
            <div className="ma__header_slim__logo">
              <SiteLogo
                url="/"
                image={{
                  src: `${process.env.NEXT_PUBLIC_BASE_PATH}/img/logo.png`,
                  alt: t('happle_alt'),
                  width: 45,
                  height: 45,
                }}
                siteName={t('title')}
                linkAriaLabel={t('site_logo_header_aria_label')}
              />
            </div>
          </div>
        </header>
      </div>
    </>
  );
};

export default Header;
